import React from 'react';
import moment from "moment";
import { AES, enc } from 'crypto-js';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { useClientView } from '../TimeTracker/TimeTrackerClient/useClientDetails.jsx';
import SettingsIcon from '../../images/icons/settings.svg';
import userCategory from '../../constants/userCategory.js';
import userTypeConsts from '../../constants/userTypeConsts';
import { setDashboardDate, setInvoiceType } from "../../features/invoice/invoiceSlice";
import { resetAccountDetails } from "../../features/accountDetails/accountDetailsSlice.js";
import { setSideBarText } from '../../features/sideBarText/sideBarTextSlice.js';
import { ReactComponent as DashboardNavIcon } from '../../images/icons/dashboardIcon.svg';
import { ReactComponent as UsersNavIcon } from '../../images/icons/usersIcon.svg';
import { ReactComponent as InvoicesNavIcon } from '../../images/icons/invoicesIcon.svg';
import { ReactComponent as TimeTrackerNavIcon } from '../../images/icons/timeTrackerIcon.svg';
import './MobilePageTitle.css';

const MobilePage = () => {
    const dispatch = useDispatch();
    const headerText = useSelector((state) => state.headerText.headerTextState);
    const user = useSelector((state) => state.auth.user);
    const { appSettings } = useSelector((state) => state.appSettings);
    const isMvp = process.env.REACT_APP_ENV_TYPE === 'mvp';
    const location = useLocation();

    const handleTabClick = () => {
        dispatch(resetAccountDetails());
        dispatch(setSideBarText(""));
        dispatch(setInvoiceType(null));
        dispatch(setDashboardDate([
            new Date(moment().tz(global.config?.getTimeZone?.() || 'UTC').subtract(30, 'years').startOf('year').format()).toISOString(),
            new Date(moment().tz(global.config?.getTimeZone?.() || 'UTC').endOf('year').format()).toISOString()
        ]));
    };

    const isClient = user?.userCategory === userCategory.Client;
    const isVendor = user?.userCategory === userCategory.Vendor;
    const isAdmin = user?.userType === userTypeConsts.SUPER_ADMIN || user?.userType === userTypeConsts.ADMIN;
    const isProjectManager = user?.userType === userTypeConsts.PROJECT_MANAGER;
    const showClientSection = isMvp && (isAdmin || isProjectManager);

    const isProjectDetails = location.pathname.includes("/client/projects/details/");
    const clientSectionActive = isProjectDetails
        ? ""
        : location.pathname.startsWith("/client/overview")
            ? "overview"
            : location.pathname.startsWith("/client/project")
                ? "projects"
                : "";
    const detailsClientId = (() => {
        if (!isProjectDetails) return "";
        const parts = location.pathname.split("/");
        const encrypted = parts[5] ? decodeURIComponent(parts[5]) : "";
        if (!encrypted) return "";
        try {
            return AES.decrypt(encrypted, "encryptionKey").toString(enc.Utf8);
        } catch (_error) {
            return "";
        }
    })();
    const { data: detailsClient } = useClientView(detailsClientId, user?.token);
    const detailsActive = detailsClient?.is_active !== false;

    return (
        <>
        {!(showClientSection && (clientSectionActive || isProjectDetails)) && (
        <div className="mobile-nav-pills-container">
            <div className="mobile-nav-pills-scroll">
                {/* Dashboard */}
                <Link
                    to={isVendor ? "/vendor/dashboard" : isClient ? "/client/dashboard" : "/user/dashboard"}
                    className={`mobile-nav-pill ${headerText === "Dashboard" ? "active" : ""}`}
                    onClick={handleTabClick}
                >
                    <span className="mobile-pill-icon">
                        <DashboardNavIcon width="15.11" height="15.11" />
                    </span>
                    <span>{global.config.locate(appSettings && appSettings.language, 'Dashboard')}</span>
                </Link>

                {/* Users / My Team (Admins only) */}
                {isAdmin && !isVendor && !isClient && (
                    <Link
                        to="/user/myteam/list"
                        className={`mobile-nav-pill ${headerText === "My Team" ? "active" : ""}`}
                        onClick={handleTabClick}
                    >
                        <span className="mobile-pill-icon">
                            <UsersNavIcon width="17.16" height="14.18" />
                        </span>
                        <span>Users</span>
                    </Link>
                )}

                {/* Invoices */}
                <Link
                    to={isVendor ? "/vendor/invoice" : isClient ? "/client/invoice" : "/user/invoices"}
                    className={`mobile-nav-pill ${headerText === "Invoices" ? "active" : ""}`}
                    onClick={handleTabClick}
                >
                    <span className="mobile-pill-icon">
                        <InvoicesNavIcon width="13" height="17" />
                    </span>
                    <span>{global.config.locate(appSettings && appSettings.language, 'Invoices')}</span>
                </Link>

                {/* Time Tracker */}
                {(user && (user.userCategory === userCategory.Individual || user.userCategory === userCategory.Business)) && (
                    <Link
                        to="/user/time-tracker/overview"
                        className={`mobile-nav-pill ${headerText === "Time Tracker" ? "active" : ""}`}
                        onClick={handleTabClick}
                    >
                        <span className="mobile-pill-icon">
                            <TimeTrackerNavIcon width="15" height="17" />
                        </span>
                        <span>{global.config.locate(appSettings && appSettings.language, 'Time_Tracker')}</span>
                    </Link>
                )}

                {/* Time Off */}
                {!isMvp && (!isVendor && !isClient) && (
                    <Link
                        to="/user/time-off/overview"
                        className={`mobile-nav-pill ${(headerText === "Time Off" || headerText === "Time Off Settings") ? "active" : ""}`}
                        onClick={handleTabClick}
                    >
                        <span className="mobile-pill-icon">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="4" width="16" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M2 8.5H18" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M6 2V5M14 2V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </span>
                        <span>{global.config.locate(appSettings && appSettings.language, 'Time_Off')}</span>
                    </Link>
                )}

                {/* Expenses */}
                {!isMvp && (isAdmin || isProjectManager) && (
                    <Link
                        to="/user/expense/overview"
                        className={`mobile-nav-pill ${headerText === "Expenses" ? "active" : ""}`}
                        onClick={handleTabClick}
                    >
                        <span className="mobile-pill-icon">
                            <svg width="19" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="1.5" y="1.5" width="17" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M1.5 5.5H18.5" stroke="currentColor" strokeWidth="1.8" />
                                <rect x="4.5" y="9.5" width="3" height="2" rx="0.5" fill="currentColor" />
                            </svg>
                        </span>
                        <span>{global.config.locate(appSettings && appSettings.language, 'Expenses')}</span>
                    </Link>
                )}
            </div>
        </div>
        )}
        {showClientSection && isProjectDetails && (
            <div className="mobile-project-identity">
                <span className="mobile-project-name">
                    <span className="mobile-project-gear">
                        <img src={SettingsIcon} alt="" />
                    </span>
                    <h1>{detailsClient?.client_name || detailsClient?.legal_entity_name || "Client"}</h1>
                </span>
                <span className={`mobile-project-status ${detailsActive ? "" : "is-off"}`}>
                    {detailsActive ? "Active" : "Inactive"}
                </span>
            </div>
        )}
        {showClientSection && clientSectionActive && (
            <div className="mobile-client-section">
                <div className="mobile-client-pills">
                    <Link
                        to="/client/overview"
                        className={`mobile-nav-pill ${clientSectionActive === "overview" ? "active" : ""}`}
                        onClick={handleTabClick}
                    >
                        <span className="mobile-pill-icon">
                            <DashboardNavIcon width="15.11" height="15.11" />
                        </span>
                        <span>{global.config.locate(appSettings && appSettings.language, 'Overview')}</span>
                    </Link>
                    <Link
                        to="/client/project"
                        className={`mobile-nav-pill ${clientSectionActive === "projects" ? "active" : ""}`}
                        onClick={handleTabClick}
                    >
                        <span className="mobile-pill-icon">
                            <svg width="16" height="16" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20.0276 4.75H17.4936V3.61746C17.4936 2.17004 16.324 1 14.8772 1H8.10742C6.66787 1 5.49831 2.17004 5.49831 3.61746V4.75H2.96435C1.87726 4.75 1 5.62759 1 6.71509V20.0351C1 21.1224 1.87726 22 2.96435 22H20.0357C21.1152 22 22 21.1224 22 20.0349L21.9998 6.71491C21.9923 5.62741 21.1151 4.74983 20.028 4.74983L20.0276 4.75ZM6.99749 3.61746C6.99749 3.00254 7.49989 2.5 8.11453 2.5H14.8843C15.4916 2.5 15.9939 3.00259 15.9939 3.61746V4.75H6.99732L6.99749 3.61746ZM2.96409 6.25H20.0277C20.2827 6.25 20.4926 6.45993 20.4926 6.71509V8.69504L11.496 11.4625L2.49935 8.69504V6.71509C2.49935 6.45995 2.7092 6.25 2.96426 6.25H2.96409ZM20.0277 20.5H2.96409C2.70905 20.5 2.49918 20.2901 2.49918 20.0349V10.2626L10.7461 12.7975V14.5001C10.7461 14.9126 11.0835 15.2501 11.4958 15.2501C11.9081 15.2501 12.2455 14.9126 12.2455 14.5001V12.8051L20.4924 10.2702V20.0425C20.4924 20.2901 20.2826 20.5 20.0275 20.5H20.0277Z" fill="currentColor" />
                            </svg>
                        </span>
                        <span>{global.config.locate(appSettings && appSettings.language, 'Projects')}</span>
                    </Link>
                </div>
            </div>
        )}
        </>
    );
};

export default MobilePage;